import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { ArrowRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { JoinBookingModalInnerQuery } from "./__generated__/JoinBookingModalInnerQuery.graphql";
import { useJoinBookingAndNavigate } from "./hooks/useJoinBookingAndNavigate";

type JoinBookingModalInnerProps = {
  isOpen: boolean;
  onClose: () => void;
  queryRef: PreloadedQuery<JoinBookingModalInnerQuery>;
  onRowPress: (slotId: string) => void;
  isWaitlistBooking: boolean;
};

export const JoinBookingModalInner = ({
  isOpen,
  onClose,
  queryRef,
  onRowPress,
  isWaitlistBooking,
}: JoinBookingModalInnerProps) => {
  const data = usePreloadedQuery(JoinBookingModalInnerQueryString, queryRef);

  const edges = data.node?.bookings?.edges ?? [];
  const joinableBookings = edges
    .filter((edge) => {
      if (!edge?.node) return false;
      return edge.node.writeAccess === "JOIN";
    })
    .map((edge) => edge?.node);

  const facility = data.node?.agenda?.facility;

  const canBookOnBusySlot = facility?.club?.supportsPrivate
    ? facility?.privateBookingPermissionSet?.canBookOnBusySlot || false
    : facility?.publicBookingPermissionSet?.canBookOnBusySlot || false;

  const handleCancel = () => {
    onClose();
  };

  const handleCreate = () => {
    onClose();
    onRowPress(data.node?.id ?? "");
  };

  const { joinBooking, isJoining } = useJoinBookingAndNavigate(onClose);

  return (
    <StandardModal
      isOpen={isOpen}
      upperTitle={t`Join Booking`}
      onClose={onClose}
      content={
        isJoining ? (
          <Loader />
        ) : (
          <Box paddingHorizontal={6}>
            <VStack gap={6}>
              <Text mb={4} variant="mainBaseMedium" color="text-secondary">
                {t`Choose whether you want to join someone else’s booking or create your own. If you join you will not be able to edit players, transportation types and rates.`}
              </Text>
              {joinableBookings?.map((booking, i) => (
                <Pressable
                  key={booking?.id ?? i}
                  onPress={() => joinBooking(booking?.id ?? "")}
                  disabled={isJoining}
                >
                  <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottomColor="surface-dividers"
                    borderBottomWidth={
                      i !== joinableBookings?.length - 1 ? 1 : 0
                    }
                    paddingVertical={6}
                  >
                    <Text>
                      {t`Join ${
                        booking?.rounds?.edges?.[0]?.node?.player?.firstName ??
                        ""
                      }'s Booking`}
                    </Text>
                    <ArrowRightIcon />
                  </HStack>
                </Pressable>
              ))}
            </VStack>
          </Box>
        )
      }
      footerContent={
        canBookOnBusySlot ? (
          <HStack
            gap={1}
            flexShrink={0}
            paddingBottom={6}
            justifyContent={"flex-end"}
          >
            <Button
              onPress={handleCancel}
              variant="secondary"
              accessibilityLabel={t`Cancel`}
              testID="join-booking-modal-cancel-button"
            >
              {t`Cancel`}
            </Button>
            {isWaitlistBooking ? null : (
              <Button
                onPress={handleCreate}
                accessibilityLabel={t`Create Separate Booking button`}
              >{t`Create Separate Booking`}</Button>
            )}
          </HStack>
        ) : null
      }
    />
  );
};

export const JoinBookingModalInnerQueryString = graphql`
  query JoinBookingModalInnerQuery($slotId: ID!) {
    node(id: $slotId) {
      ... on QueueSlot {
        agenda {
          facility {
            club {
              supportsPrivate
            }
            privateBookingPermissionSet {
              canBookOnBusySlot
            }
            publicBookingPermissionSet {
              canBookOnBusySlot
            }
          }
        }
        id
        bookings(first: 1000) {
          edges {
            node {
              writeAccess
              id
              rounds {
                edges {
                  node {
                    player {
                      id
                      firstName
                    }
                  }
                }
              }
            }
          }
        }
      }
      ... on TimeSlot {
        agenda {
          facility {
            club {
              supportsPrivate
            }
            privateBookingPermissionSet {
              canBookOnBusySlot
            }
            publicBookingPermissionSet {
              canBookOnBusySlot
            }
          }
        }
        id
        bookings(first: 1000) {
          edges {
            node {
              writeAccess
              id
              rounds {
                edges {
                  node {
                    player {
                      id
                      firstName
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
